/**
 * @file Obfuscated photos with Waldo icons (blockers) on top of faces.
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Photo from '../Photo';
import PhotoFaceBlock from './PhotoFaceBlock';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import { IMAGE_BLUR_TYPES } from '../../../constants/image-blur-types';

const propTypes = {
  id: PropTypes.string.isRequired,
  thumbs: PropTypes.arrayOf(
    PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      url: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
  imgClassName: PropTypes.string,
  faceboxes: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onMouseDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  onClick: PropTypes.func,
  obfuscatorMultiplier: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  maxThumbnailSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
  minThumbnailSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
  blockerMinSizeThreshold: PropTypes.number,
  blockerMinSize: PropTypes.number,
  blockerMaxSize: PropTypes.number,
  shouldUseFaceblockedUrls: PropTypes.bool,
  faceblockerImageUrl: PropTypes.string,
  enableFaceblock: PropTypes.bool,
};

const PhotoObfuscated = ({
  id,
  thumbs,
  className,
  imgClassName,
  faceboxes,
  children,
  obfuscatorMultiplier,
  maxThumbnailSize,
  minThumbnailSize = IMAGE_SIZE_CODE.LARGE,
  blockerMinSizeThreshold,
  blockerMinSize,
  blockerMaxSize,
  onMouseDown: onMouseDownProp,
  onMouseUp: onMouseUpProp,
  onClick,
  shouldUseFaceblockedUrls,
  faceblockerImageUrl,
  enableFaceblock,
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  const frontThumbs = useMemo(() => {
    if (shouldUseFaceblockedUrls) {
      return thumbs.filter(
        thumb => thumb.blur_type === IMAGE_BLUR_TYPES.FOREGROUND,
      );
    }

    return thumbs;
  }, [thumbs, shouldUseFaceblockedUrls]);

  const backThumbs = useMemo(() => {
    if (shouldUseFaceblockedUrls) {
      return thumbs.filter(
        thumb => thumb.blur_type === IMAGE_BLUR_TYPES.BACKGROUND,
      );
    }

    return thumbs;
  }, [thumbs, shouldUseFaceblockedUrls]);

  const onImageLoaded = () => {
    setImageLoading(false);
  };

  const onMouseDown = () => {
    setTouched(true);
    onMouseDownProp?.();
  };

  const onMouseUp = () => {
    setTouched(false);
    onMouseUpProp?.();
  };

  const classes = classNames({
    'photo-obfuscated': enableFaceblock,
    clickable: !!onClick,
    toggled: touched,
    [className]: className,
    'photo-obfuscated-loading': imageLoading,
  });

  const frontClasses = classNames({
    'photo-obfuscated-photo photo-obfuscated-front': true,
    'faceblocked-thumbnail-front ': shouldUseFaceblockedUrls,
    'faceblocked-thumbnail-front-touched': shouldUseFaceblockedUrls && touched,
  });

  const backClasses = classNames({
    'photo-obfuscated-photo photo-obfuscated-back': true,
    'faceblocked-thumbnail-back': shouldUseFaceblockedUrls,
    'd-none': obfuscatorMultiplier === 0,
  });

  return (
    <div className={classes}>
      <div // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        className="photo-obfuscated-target"
        onMouseDown={onMouseDown}
        onTouchStart={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onMouseUp}
        onClick={onClick}
      >
        <div className="photo-obfuscated-loading-bg" />

        <Photo
          thumbs={frontThumbs}
          className={frontClasses}
          imgClassName={imgClassName}
          style={{
            clipPath:
              !shouldUseFaceblockedUrls && touched
                ? `url("#clip-${id}")`
                : 'none',
            WebkitClipPath:
              !shouldUseFaceblockedUrls && touched
                ? `url("#clip-${id}")`
                : 'none',
          }}
          onImageLoaded={onImageLoaded}
          maxThumbnailSize={maxThumbnailSize}
          minThumbnailSize={minThumbnailSize}
          {...props}
        />

        <Photo
          thumbs={backThumbs}
          className={backClasses}
          imgClassName={imgClassName}
          maxThumbnailSize={maxThumbnailSize}
          minThumbnailSize={minThumbnailSize}
          {...props}
        />

        {enableFaceblock && (
          <PhotoFaceBlock
            id={id}
            faceboxes={faceboxes}
            obfuscatorMultiplier={obfuscatorMultiplier}
            renderSvg={!shouldUseFaceblockedUrls && touched}
            blockerMinSizeThreshold={blockerMinSizeThreshold}
            blockerMinSize={blockerMinSize}
            blockerMaxSize={blockerMaxSize}
            faceblockerImageUrl={faceblockerImageUrl}
          />
        )}
      </div>
      {children}
    </div>
  );
};

PhotoObfuscated.propTypes = propTypes;

export default PhotoObfuscated;
